import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceDot,
} from "recharts";
import { Package, ShieldCheck, TrendingDown, Archive, FileDown } from "lucide-react";

const EVENT_META = {
  acquisition: { label: "Anschaffung", Icon: ShieldCheck, color: "#059669", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
  depreciation: { label: "Abschreibung", Icon: TrendingDown, color: "#0369a1", bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-800" },
  disposal: { label: "Abgang", Icon: Archive, color: "#e11d48", bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800" },
};

function fmt(v) {
  const n = Number(v || 0);
  return n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AssetTimelineDialog({ open, onClose, assetId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!open || !assetId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiClient.get(`/assets/${assetId}/timeline`);
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.detail || "Timeline konnte nicht geladen werden.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, assetId]);

  const downloadPdf = async () => {
    if (!assetId) return;
    setDownloadingPdf(true);
    try {
      const res = await apiClient.get(`/assets/${assetId}/timeline/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (data?.asset_name || "Anlage").replace(/\s+/g, "_");
      a.download = `Timeline_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || "PDF-Download fehlgeschlagen.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const chartData = (data?.events || []).map((e) => ({
    date: e.date,
    bv: e.book_value_after,
    type: e.event_type,
    label: e.description,
  }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="asset-timeline-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-700" />
                Anlage-Timeline
                {data && (
                  <span className="text-sm font-normal text-slate-500 ml-1 truncate">
                    · {data.asset_name}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {data && (
                  <>
                    Anschaffungskosten <span className="font-mono">{fmt(data.acquisition_cost)} {data.currency}</span>
                    {" · "}Aktueller Buchwert <span className="font-mono">{fmt(data.current_book_value)}</span>
                    {" · "}Kum. Abschreibungen <span className="font-mono">{fmt(data.current_accumulated_depreciation)}</span>
                    {" · "}Nutzungsdauer <span className="font-mono">{data.useful_life_years} J.</span>
                  </>
                )}
              </DialogDescription>
            </div>
            {data && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadPdf}
                disabled={downloadingPdf}
                data-testid="asset-timeline-pdf-button"
                className="shrink-0"
              >
                <FileDown className="h-3.5 w-3.5 mr-1.5" />
                {downloadingPdf ? "Lädt ..." : "PDF"}
              </Button>
            )}
          </div>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center text-xs text-slate-500" data-testid="asset-timeline-loading">
            Lade Timeline…
          </div>
        )}
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="asset-timeline-error">
            {error}
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5">
            {/* Buchwert-Chart */}
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="asset-timeline-chart">
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                Buchwert-Entwicklung ({data.currency})
              </div>
              <div style={{ width: "100%", minWidth: 0, height: 220 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200} initialDimension={{ width: 800, height: 200 }}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#475569" }} width={60} />
                    <Tooltip
                      contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #cbd5e1" }}
                      formatter={(v) => [`${fmt(v)} ${data.currency}`, "Buchwert"]}
                      labelFormatter={(d) => `Datum: ${d}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="bv"
                      stroke="#0f172a"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#0f172a" }}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                    {/* Highlight-Dots für Dispose/Acquisition */}
                    {chartData.map((pt, i) =>
                      pt.type !== "depreciation" ? (
                        <ReferenceDot
                          key={`rd-${i}`}
                          x={pt.date}
                          y={pt.bv}
                          r={6}
                          fill={EVENT_META[pt.type]?.color || "#0f172a"}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      ) : null
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Event-Liste */}
            <div data-testid="asset-timeline-events">
              <div className="text-xs font-semibold text-slate-700 mb-2">
                Ereignisse ({data.events.length})
              </div>
              <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
                {data.events.map((e, i) => {
                  const meta = EVENT_META[e.event_type] || EVENT_META.depreciation;
                  const Icon = meta.Icon;
                  return (
                    <li key={i} className="pl-4 relative" data-testid={`asset-timeline-event-${e.event_type}-${i}`}>
                      <span className={`absolute -left-[11px] top-0 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${meta.bg}`}>
                        <Icon className={`h-3 w-3 ${meta.text}`} />
                      </span>
                      <div className="flex items-baseline justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-slate-500">{formatDate(e.date)}</span>
                            <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${meta.border} ${meta.bg} ${meta.text}`}>
                              {meta.label}
                            </span>
                          </div>
                          <div className="text-sm text-slate-800 mt-0.5">{e.description}</div>
                        </div>
                        <div className="text-right">
                          {e.event_type !== "disposal" && (
                            <div className={`text-xs font-mono ${e.event_type === "acquisition" ? "text-emerald-700" : "text-sky-700"}`}>
                              {e.event_type === "acquisition" ? "+" : "−"}{fmt(e.amount)}
                            </div>
                          )}
                          {e.event_type === "disposal" && e.amount > 0 && (
                            <div className="text-xs font-mono text-emerald-700">+{fmt(e.amount)}</div>
                          )}
                          <div className="text-[10px] text-slate-500">
                            BV: <span className="font-mono font-semibold text-slate-700">{fmt(e.book_value_after)}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
